import { useState, useEffect } from "react";
import axios from "axios";
import { Shield, Users, TrendingUp, Copy, Check, Lock, Trash2, RotateCcw, Upload, RefreshCw, Zap } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [accessCode, setAccessCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [scores, setScores] = useState({ round1: "", round2: "", round3: "", round4: "" });
  const [missedCut, setMissedCut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("participants");
  const [batchRound, setBatchRound] = useState("1");
  const [batchText, setBatchText] = useState("");
  const [batchResult, setBatchResult] = useState(null);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [participantsRes, playersRes] = await Promise.all([
        axios.get(`${API}/admin/participants`),
        axios.get(`${API}/players`)
      ]);
      setParticipants(participantsRes.data);
      setPlayers(playersRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const deleteParticipant = async (participantId, participantName) => {
    if (!window.confirm(`Are you sure you want to remove ${participantName} from the pool?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/participants/${participantId}`);
      alert("Participant removed successfully!");
      fetchData();
    } catch (error) {
      alert("Failed to remove participant");
    }
  };

  const resetPicks = async (participantId, participantName) => {
    if (!window.confirm(`Reset picks for ${participantName}? This will clear their selections.`)) {
      return;
    }
    try {
      await axios.post(`${API}/admin/participants/${participantId}/reset-picks`);
      alert("Picks reset successfully!");
      fetchData();
    } catch (error) {
      alert("Failed to reset picks");
    }
  };

  const generateCode = async () => {
    try {
      const response = await axios.post(`${API}/admin/generate-code`);
      setAccessCode(response.data.access_code);
    } catch (error) {
      alert("Failed to generate code");
    }
  };

  const copyCode = () => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(accessCode)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          // Fallback to execCommand
          fallbackCopy();
        });
    } else {
      // Use fallback method
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    const textArea = document.createElement('textarea');
    textArea.value = accessCode;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    document.body.removeChild(textArea);
  };

  const lockPicks = async () => {
    if (!window.confirm("Are you sure you want to lock all picks? This cannot be undone!")) {
      return;
    }
    try {
      await axios.post(`${API}/admin/lock-picks`);
      alert("All picks have been locked!");
      fetchData();
    } catch (error) {
      alert("Failed to lock picks");
    }
  };

  const unlockPicks = async () => {
    if (!window.confirm("Are you sure you want to unlock all picks? Participants will be able to edit their selections again.")) {
      return;
    }
    try {
      await axios.post(`${API}/admin/unlock-picks`);
      alert("All picks have been unlocked!");
      fetchData();
    } catch (error) {
      alert("Failed to unlock picks");
    }
  };

  const handlePlayerSelection = (playerId) => {
    setSelectedPlayer(playerId);
    if (playerId) {
      const player = players.find(p => p.id === playerId);
      if (player) {
        setScores({
          round1: player.round1_score != null ? String(player.round1_score) : "",
          round2: player.round2_score != null ? String(player.round2_score) : "",
          round3: player.round3_score != null ? String(player.round3_score) : "",
          round4: player.round4_score != null ? String(player.round4_score) : "",
        });
        setMissedCut(player.missed_cut || false);
      }
    } else {
      setScores({ round1: "", round2: "", round3: "", round4: "" });
      setMissedCut(false);
    }
  };

  const updateScore = async () => {
    if (!selectedPlayer) {
      alert("Please select a player");
      return;
    }

    try {
      const payload = {
        player_id: selectedPlayer,
        round1: scores.round1 ? parseInt(scores.round1) : null,
        round2: scores.round2 ? parseInt(scores.round2) : null,
        round3: scores.round3 ? parseInt(scores.round3) : null,
        round4: scores.round4 ? parseInt(scores.round4) : null,
        missed_cut: missedCut
      };

      await axios.post(`${API}/admin/scores`, payload);
      alert("Score updated successfully!");
      setScores({ round1: "", round2: "", round3: "", round4: "" });
      setSelectedPlayer("");
      setMissedCut(false);
      fetchData();
    } catch (error) {
      alert("Failed to update score");
    }
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : "Unknown";
  };

  const parseBatchText = (text) => {
    const lines = text.trim().split("\n").filter(l => l.trim());
    const parsed = [];
    for (const line of lines) {
      // Match patterns like: "Player Name 70", "Player Name: 70", "Player Name - 70", "70 Player Name"
      // Also handle "Player Name  +2" or "Player Name  -5" (relative to par 72)
      const trimmed = line.trim();
      
      // Try: score at end (most common paste format)
      const endMatch = trimmed.match(/^(.+?)\s+(-?\d+)\s*$/);
      if (endMatch) {
        const name = endMatch[1].replace(/[:\-|]+$/, "").trim();
        const score = parseInt(endMatch[2]);
        // If score is small (between -20 and +20), it's likely relative to par
        if (score >= -20 && score <= 20) {
          parsed.push({ player_name: name, score: 72 + score });
        } else {
          parsed.push({ player_name: name, score });
        }
        continue;
      }
      
      // Try: score at beginning
      const startMatch = trimmed.match(/^(-?\d+)\s+(.+)$/);
      if (startMatch) {
        const score = parseInt(startMatch[1]);
        const name = startMatch[2].trim();
        if (score >= -20 && score <= 20) {
          parsed.push({ player_name: name, score: 72 + score });
        } else {
          parsed.push({ player_name: name, score });
        }
        continue;
      }
    }
    return parsed;
  };

  const handleBatchSubmit = async () => {
    if (!batchText.trim()) {
      alert("Please paste score data first");
      return;
    }

    const parsed = parseBatchText(batchText);
    if (parsed.length === 0) {
      alert("Could not parse any scores. Use format:\nPlayer Name 70\nor\nPlayer Name -2");
      return;
    }

    setBatchSubmitting(true);
    setBatchResult(null);
    try {
      const response = await axios.post(`${API}/admin/scores/batch`, {
        round_number: parseInt(batchRound),
        scores: parsed
      });
      setBatchResult(response.data);
      fetchData();
    } catch (error) {
      alert("Failed to batch update: " + (error.response?.data?.detail || error.message));
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleSyncLive = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await axios.post(`${API}/admin/scores/auto-fetch`);
      setSyncResult(response.data);
      setLastSynced(new Date().toLocaleTimeString());
      fetchData();
    } catch (error) {
      setSyncResult({ error: error.response?.data?.detail || "Failed to sync" });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#006747] mx-auto mb-4"></div>
          <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FDFBF7" }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#006747]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#006747] rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1C2A21]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Admin Dashboard
              </h1>
              <p className="text-sm text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>Masters Pool 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Live Sync Banner */}
        <div data-testid="live-sync-banner" className="mb-6 p-4 bg-white rounded-xl border border-[#E2E8E4] shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#006747] rounded-full flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-[#1C2A21]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                  ESPN Live Score Sync
                </p>
                <p className="text-xs text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                  {lastSynced ? `Last synced: ${lastSynced}` : "Pull live Masters scores from ESPN"}
                </p>
              </div>
            </div>
            <button
              data-testid="sync-live-button"
              onClick={handleSyncLive}
              disabled={syncing}
              className="px-5 py-2.5 bg-[#006747] hover:bg-[#005238] text-white font-semibold rounded transition-all duration-300 shadow-sm disabled:opacity-60 flex items-center space-x-2"
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} />
              <span>{syncing ? "Syncing..." : "Sync Live Scores"}</span>
            </button>
          </div>
          {syncResult && !syncResult.error && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-semibold text-green-800 text-sm" style={{ fontFamily: "'Figtree', sans-serif" }}>
                {syncResult.message}
              </p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-green-700" style={{ fontFamily: "'Figtree', sans-serif" }}>
                <span>R1: {syncResult.rounds_updated?.r1 || 0} players</span>
                <span>R2: {syncResult.rounds_updated?.r2 || 0} players</span>
                <span>R3: {syncResult.rounds_updated?.r3 || 0} players</span>
                <span>R4: {syncResult.rounds_updated?.r4 || 0} players</span>
              </div>
              {syncResult.not_found?.length > 0 && (
                <p className="text-xs text-yellow-700 mt-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                  {syncResult.not_found.length} ESPN players not in pool
                </p>
              )}
            </div>
          )}
          {syncResult?.error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700" style={{ fontFamily: "'Figtree', sans-serif" }}>{syncResult.error}</p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-[#E2E8E4]">
          <div className="flex space-x-8">
            <button
              data-testid="participants-tab"
              onClick={() => setActiveTab("participants")}
              className={`pb-3 px-1 font-semibold transition-all ${
                activeTab === "participants"
                  ? "border-b-2 border-[#006747] text-[#006747]"
                  : "text-[#8A9A92] hover:text-[#4A5B52]"
              }`}
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              Participants
            </button>
            <button
              data-testid="scores-tab"
              onClick={() => setActiveTab("scores")}
              className={`pb-3 px-1 font-semibold transition-all ${
                activeTab === "scores"
                  ? "border-b-2 border-[#006747] text-[#006747]"
                  : "text-[#8A9A92] hover:text-[#4A5B52]"
              }`}
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              Update Scores
            </button>
            <button
              data-testid="batch-tab"
              onClick={() => setActiveTab("batch")}
              className={`pb-3 px-1 font-semibold transition-all ${
                activeTab === "batch"
                  ? "border-b-2 border-[#006747] text-[#006747]"
                  : "text-[#8A9A92] hover:text-[#4A5B52]"
              }`}
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              Batch Import
            </button>
            <button
              data-testid="access-tab"
              onClick={() => setActiveTab("access")}
              className={`pb-3 px-1 font-semibold transition-all ${
                activeTab === "access"
                  ? "border-b-2 border-[#006747] text-[#006747]"
                  : "text-[#8A9A92] hover:text-[#4A5B52]"
              }`}
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              Access Codes
            </button>
          </div>
        </div>

        {/* Participants Tab */}
        {activeTab === "participants" && (
          <div data-testid="participants-section">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Pool Participants
                </h2>
                <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                  {participants.length} participants registered
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  data-testid="unlock-picks-button"
                  onClick={unlockPicks}
                  className="px-6 py-3 bg-[#006747] hover:bg-[#005238] text-white font-semibold rounded transition-all duration-300 shadow-sm flex items-center space-x-2"
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  <Lock className="w-4 h-4" strokeWidth={1.5} />
                  <span>Unlock All Picks</span>
                </button>
                <button
                  data-testid="lock-picks-button"
                  onClick={lockPicks}
                  className="px-6 py-3 bg-[#F2A900] hover:bg-[#D99700] text-[#1A1F16] font-semibold rounded transition-all duration-300 shadow-sm flex items-center space-x-2"
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  <Lock className="w-4 h-4" strokeWidth={1.5} />
                  <span>Lock All Picks</span>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#F3F5F1]">
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Name</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Picks</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Tiebreaker</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Status</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.id} data-testid={`participant-row-${p.id}`} className="border-t border-[#E2E8E4] hover:bg-[#F3F5F1]">
                        <td className="px-6 py-4 font-semibold text-[#1C2A21]" style={{ fontFamily: "'Figtree', sans-serif" }}>{p.name}</td>
                        <td className="px-6 py-4">
                          {p.picks && p.picks.length > 0 ? (
                            <div className="text-sm text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                              {p.picks.map(pick => pick.player_name).join(", ")}
                            </div>
                          ) : (
                            <span className="text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>No picks yet</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                          {p.tiebreaker !== null && p.tiebreaker !== undefined ? `${p.tiebreaker}` : "-"}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                              p.picks_locked
                                ? "bg-red-100 text-red-700"
                                : p.picks && p.picks.length === 6
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                            style={{ fontFamily: "'Figtree', sans-serif" }}
                          >
                            {p.picks_locked ? "Locked" : p.picks && p.picks.length === 6 ? "Complete" : "Pending"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {p.picks && p.picks.length > 0 && !p.picks_locked && (
                              <button
                                data-testid={`reset-picks-${p.id}`}
                                onClick={() => resetPicks(p.id, p.name)}
                                className="p-2 text-[#F2A900] hover:bg-[#F2A900]/10 rounded transition-all"
                                title="Reset picks"
                              >
                                <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            )}
                            <button
                              data-testid={`delete-participant-${p.id}`}
                              onClick={() => deleteParticipant(p.id, p.name)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition-all"
                              title="Remove from pool"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Scores Tab */}
        {activeTab === "scores" && (
          <div data-testid="scores-section">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Update Player Scores
              </h2>
              <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                Enter scores after each round
              </p>
            </div>

            <div className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    Select Player
                  </label>
                  <select
                    data-testid="player-select"
                    value={selectedPlayer}
                    onChange={(e) => handlePlayerSelection(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all"
                    style={{ fontFamily: "'Figtree', sans-serif" }}
                  >
                    <option value="">Choose a player...</option>
                    {players.map((player) => (
                      <option key={player.id} value={player.id}>
                        {player.name} (Tier {player.tier}) {player.round1_score != null ? `- R1: ${player.round1_score}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      Round 1
                    </label>
                    <input
                      data-testid="round1-input"
                      type="number"
                      value={scores.round1}
                      onChange={(e) => setScores({ ...scores, round1: e.target.value })}
                      placeholder="e.g., 68"
                      className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all"
                      style={{ fontFamily: "'Figtree', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      Round 2
                    </label>
                    <input
                      data-testid="round2-input"
                      type="number"
                      value={scores.round2}
                      onChange={(e) => setScores({ ...scores, round2: e.target.value })}
                      placeholder="e.g., 71"
                      className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all"
                      style={{ fontFamily: "'Figtree', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      Round 3
                    </label>
                    <input
                      data-testid="round3-input"
                      type="number"
                      value={scores.round3}
                      onChange={(e) => setScores({ ...scores, round3: e.target.value })}
                      placeholder="e.g., 69"
                      disabled={missedCut}
                      className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all disabled:opacity-50 disabled:bg-gray-100"
                      style={{ fontFamily: "'Figtree', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      Round 4
                    </label>
                    <input
                      data-testid="round4-input"
                      type="number"
                      value={scores.round4}
                      onChange={(e) => setScores({ ...scores, round4: e.target.value })}
                      placeholder="e.g., 70"
                      disabled={missedCut}
                      className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all disabled:opacity-50 disabled:bg-gray-100"
                      style={{ fontFamily: "'Figtree', sans-serif" }}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 p-4 bg-[#F3F5F1] rounded-lg">
                  <input
                    data-testid="missed-cut-checkbox"
                    type="checkbox"
                    id="missedCut"
                    checked={missedCut}
                    onChange={(e) => setMissedCut(e.target.checked)}
                    className="w-4 h-4 text-[#006747] border-[#E2E8E4] rounded focus:ring-[#006747]"
                  />
                  <label htmlFor="missedCut" className="text-sm text-[#1C2A21]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    <span className="font-semibold">Missed Cut</span> - Automatically assigns 80 for rounds 3 & 4
                  </label>
                </div>

                <button
                  data-testid="update-score-button"
                  onClick={updateScore}
                  disabled={!selectedPlayer}
                  className="px-6 py-3 bg-[#006747] hover:bg-[#005238] text-white font-semibold rounded transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  Update Score
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Access Codes Tab */}
        {activeTab === "batch" && (
          <div data-testid="batch-section">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Batch Import Scores
              </h2>
              <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                Paste all player scores at once instead of updating one by one
              </p>
            </div>

            <div className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    Select Round
                  </label>
                  <div className="flex space-x-3">
                    {["1", "2", "3", "4"].map((r) => (
                      <button
                        key={r}
                        data-testid={`batch-round-${r}`}
                        onClick={() => setBatchRound(r)}
                        className={`px-5 py-2.5 rounded font-semibold transition-all ${
                          batchRound === r
                            ? "bg-[#006747] text-white"
                            : "bg-[#F3F5F1] text-[#4A5B52] hover:bg-[#E2E8E4]"
                        }`}
                        style={{ fontFamily: "'Figtree', sans-serif" }}
                      >
                        Round {r}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    Paste Scores
                  </label>
                  <p className="text-xs text-[#8A9A92] mb-3" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    One player per line. Accepted formats: <code className="bg-[#F3F5F1] px-1 rounded">Player Name 70</code> or <code className="bg-[#F3F5F1] px-1 rounded">Player Name -2</code> (relative to par 72)
                  </p>
                  <textarea
                    data-testid="batch-text-input"
                    value={batchText}
                    onChange={(e) => setBatchText(e.target.value)}
                    placeholder={"Scottie Scheffler 70\nRory McIlroy 67\nBryson DeChambeau 76\n\nor relative to par:\nScottie Scheffler -2\nRory McIlroy -5"}
                    rows={12}
                    className="w-full px-4 py-3 border border-[#E2E8E4] rounded font-mono text-sm focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all"
                  />
                </div>

                {batchText.trim() && (
                  <div className="p-4 bg-[#F3F5F1] rounded-lg">
                    <p className="text-sm font-semibold text-[#1C2A21] mb-1" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      Preview: {parseBatchText(batchText).length} scores detected
                    </p>
                    <div className="text-xs text-[#4A5B52] max-h-32 overflow-y-auto space-y-0.5" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      {parseBatchText(batchText).slice(0, 10).map((s, i) => (
                        <div key={i}>{s.player_name}: {s.score}</div>
                      ))}
                      {parseBatchText(batchText).length > 10 && (
                        <div className="text-[#8A9A92]">...and {parseBatchText(batchText).length - 10} more</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-4">
                  <button
                    data-testid="batch-submit-button"
                    onClick={handleBatchSubmit}
                    disabled={batchSubmitting || !batchText.trim()}
                    className="px-6 py-3 bg-[#006747] hover:bg-[#005238] text-white font-semibold rounded transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    style={{ fontFamily: "'Figtree', sans-serif" }}
                  >
                    <Upload className="w-4 h-4" strokeWidth={1.5} />
                    <span>{batchSubmitting ? "Importing..." : `Import Round ${batchRound} Scores`}</span>
                  </button>
                  {batchText.trim() && (
                    <button
                      data-testid="batch-clear-button"
                      onClick={() => { setBatchText(""); setBatchResult(null); }}
                      className="px-4 py-3 text-[#4A5B52] hover:text-[#1C2A21] font-medium transition-all"
                      style={{ fontFamily: "'Figtree', sans-serif" }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {batchResult && (
                  <div className={`p-4 rounded-lg border ${batchResult.not_found?.length > 0 ? "bg-yellow-50 border-yellow-200" : "bg-green-50 border-green-200"}`}>
                    <p className="font-semibold text-[#1C2A21] mb-1" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      {batchResult.message}
                    </p>
                    {batchResult.not_found?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-yellow-800 font-medium" style={{ fontFamily: "'Figtree', sans-serif" }}>
                          Players not found ({batchResult.not_found.length}):
                        </p>
                        <p className="text-xs text-yellow-700 mt-1" style={{ fontFamily: "'Figtree', sans-serif" }}>
                          {batchResult.not_found.join(", ")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "access" && (
          <div data-testid="access-section">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Generate Access Code
              </h2>
              <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                Create a code to share with participants
              </p>
            </div>

            <div className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm p-6">
              <div className="space-y-6">
                <button
                  data-testid="generate-code-button"
                  onClick={generateCode}
                  className="px-6 py-3 bg-[#006747] hover:bg-[#005238] text-white font-semibold rounded transition-all duration-300 shadow-sm"
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  Generate New Code
                </button>

                {accessCode && (
                  <div className="p-6 bg-[#F3F5F1] rounded-lg">
                    <p className="text-sm text-[#4A5B52] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>Access Code:</p>
                    <div className="flex items-center space-x-3">
                      <code data-testid="access-code-display" className="flex-1 px-4 py-3 bg-white rounded border border-[#E2E8E4] text-lg font-mono text-[#1C2A21]">
                        {accessCode}
                      </code>
                      <button
                        data-testid="copy-code-button"
                        onClick={copyCode}
                        className="p-3 bg-[#006747] hover:bg-[#005238] text-white rounded transition-all"
                      >
                        {copied ? <Check className="w-5 h-5" strokeWidth={1.5} /> : <Copy className="w-5 h-5" strokeWidth={1.5} />}
                      </button>
                    </div>
                    <p className="mt-4 text-sm text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      Share this link with participants:
                    </p>
                    <code className="block mt-2 px-4 py-3 bg-white rounded border border-[#E2E8E4] text-sm text-[#1C2A21] break-all">
                      {window.location.origin}/?code={accessCode}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;